import { useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { motion } from "framer-motion";
import { Send, Mail, CheckCircle, AlertCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sectionAnimation = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const feedbackAnimation = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
};

export default function ContactForm() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [status, setStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) {
      setConsentError(true);
      return;
    }
    setConsentError(false);
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm({ name: "", email: "", message: "" });
      setTimeout(() => setStatus(null), 4000);
    } catch {
      setStatus("error");
      setTimeout(() => setStatus(null), 4000);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-16 md:py-20 bg-[#0a0f1f]">
      <div className="max-w-2xl mx-auto px-6 md:px-12">
        <motion.div
          {...sectionAnimation}
        >
          <h2
            data-testid="contact-h2"
            className="font-playfair text-4xl md:text-5xl text-[#f8f9fa] tracking-tight mb-4 text-center"
          >
            {t("contact.h2")}
          </h2>

          <p className="font-outfit text-sm text-[#94a3b8] text-center mb-10">
            {t("contact.info")}{" "}
            <a href="mailto:info@sitayag.com" className="text-[#1a6cf5] hover:underline">
              info@sitayag.com
            </a>
          </p>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
            <div>
              <label className="font-outfit text-sm text-[#94a3b8] mb-2 block">{t("contact.name")}</label>
              <input
                type="text"
                required
                data-testid="contact-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-[#131b2f] border border-[#2a3449] rounded-xl px-5 py-3.5 font-outfit text-[#f8f9fa] placeholder-[#64748b] focus:outline-none focus:border-[#1a6cf5]/50 focus:ring-1 focus:ring-[#1a6cf5]/20 transition-all"
                placeholder={t("contact.name")}
              />
            </div>

            <div>
              <label className="font-outfit text-sm text-[#94a3b8] mb-2 block">{t("contact.email")}</label>
              <input
                type="email"
                required
                data-testid="contact-email-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-[#131b2f] border border-[#2a3449] rounded-xl px-5 py-3.5 font-outfit text-[#f8f9fa] placeholder-[#64748b] focus:outline-none focus:border-[#1a6cf5]/50 focus:ring-1 focus:ring-[#1a6cf5]/20 transition-all"
                placeholder={t("contact.email")}
              />
            </div>

            <div>
              <label className="font-outfit text-sm text-[#94a3b8] mb-2 block">{t("contact.message")}</label>
              <textarea
                required
                rows={5}
                data-testid="contact-message-input"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full bg-[#131b2f] border border-[#2a3449] rounded-xl px-5 py-3.5 font-outfit text-[#f8f9fa] placeholder-[#64748b] focus:outline-none focus:border-[#1a6cf5]/50 focus:ring-1 focus:ring-[#1a6cf5]/20 transition-all resize-none"
                placeholder={t("contact.message")}
              />
            </div>

            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="consent"
                data-testid="contact-consent-checkbox"
                checked={consent}
                onChange={(e) => { setConsent(e.target.checked); setConsentError(false); }}
                className="mt-1 w-4 h-4 accent-[#1a6cf5] rounded border-[#2a3449] bg-[#131b2f] shrink-0"
              />
              <label htmlFor="consent" className="font-outfit text-sm text-[#94a3b8]">
                {t("contact.consent")}{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#1a6cf5] hover:underline">
                  {t("contact.consent_link")}
                </a>
              </label>
            </div>
            {consentError && (
              <p data-testid="contact-consent-error" className="text-red-400 text-sm font-outfit">
                {t("contact.consent_required")}
              </p>
            )}

            <button
              type="submit"
              data-testid="contact-submit-btn"
              disabled={status === "loading"}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#1a6cf5] hover:bg-[#3b82f6] disabled:opacity-50 text-white font-outfit px-8 py-4 rounded-full transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#1a6cf5]/25"
            >
              {status === "loading" ? (
                <span className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <Send size={18} />
              )}
              {t("contact.submit")}
            </button>

            {status === "success" && (
              <motion.div
                {...feedbackAnimation}
                data-testid="contact-success"
                className="flex items-center gap-2 justify-center text-green-400 font-outfit text-sm"
              >
                <CheckCircle size={16} /> {t("contact.success")}
              </motion.div>
            )}
            {status === "error" && (
              <motion.div
                {...feedbackAnimation}
                data-testid="contact-error"
                className="flex items-center gap-2 justify-center text-red-400 font-outfit text-sm"
              >
                <AlertCircle size={16} /> {t("contact.error")}
              </motion.div>
            )}
          </form>
        </motion.div>
      </div>
    </section>
  );
}
